import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Divider, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import cloneDeep from 'lodash.clonedeep';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
  IBudget,
  IFacility,
  IMonthlyDetail,
  IPage,
} from 'common/src/types/interfaces';

import { getFacilities } from 'portal/src/api/FacilitiesService';
import useAuth from 'portal/src/auth/useAuth';
import { FormCard } from 'portal/src/components/common/forms/FormCard';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import { numberFormatter } from 'portal/src/utils/numberFormatter';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import { AddFacilityForm } from './AddFacilityForm';
import { FiscalMonthSelector } from './FiscalMonthSelector';
import { ViewMonthDetails } from './ViewMonthDetails';

import {
  dateFormatter,
  MonthlyCostSummary,
  MonthlyFacilityCost,
} from '../../common';

export const BudgetSummary = ({
  fiscalYear,
  budget,
  onChange,
}: {
  fiscalYear: number;
  budget: Partial<IBudget>;
  onChange: (budget: IBudget) => void;
}) => {
  const { hasReadOnlyAccess } = useAuth();
  const fiscalMonth = useSearchParams()[0].get('month') ?? 6;
  const snackbarHandler = useSnackbarHandler();
  const isReadonly = hasReadOnlyAccess();

  const { data: facilities, isError } = useQuery({
    queryKey: ['facilities'],
    queryFn: async () => await getFacilities(),
    initialData: { data: [] } as IPage<IFacility>,
  });

  const [fiscalMonthSelected, setFiscalMonthSelected] = useState<number>();
  const [formRows, setFormRows] = useState([]);
  const [formFacilityId, setFormFacilityId] = useState('');
  const [showFacilityForm, setFacilityForm] = useState(false);

  const monthlyCosts: MonthlyCostSummary[] = useMemo(() => {
    const months = [];
    const budgetPeriod = new Date(fiscalYear - 1, Number(fiscalMonth), 1);
    for (let month = 0; month < 12; month++) {
      const fMonth = month % 12;
      const mDetails = budget.monthlyDetails.filter(
        (md) => md.month === fMonth,
      );

      const total = mDetails.reduce((acc, val) => acc + val.cost, 0);

      months.push({
        id: fMonth,
        month: fMonth,
        period: budgetPeriod.toISOString(),
        cost: total,
      } as MonthlyCostSummary);
      budgetPeriod.setMonth(budgetPeriod.getMonth() + 1);
    }

    return months;
  }, [budget, fiscalMonth, fiscalYear]);

  const getSelectedMonthlyDetails = useMemo(
    () =>
      function (): IMonthlyDetail[] {
        const fMonth = budget.monthlyDetails.filter(
          (md) => md.month === fiscalMonthSelected,
        );
        return fMonth;
      },
    [budget.monthlyDetails, fiscalMonthSelected],
  );

  const monthlyFacilityDetails: MonthlyFacilityCost[] = useMemo(() => {
    const monthlyDetails = getSelectedMonthlyDetails();
    if (monthlyDetails && monthlyDetails.length) {
      const data = {};
      monthlyDetails.forEach((md) => {
        if (data[md.facilityId]) {
          const d = data[md.facilityId];
          d.totalCosts += md.cost;
          if (!d.services.includes(md.serviceType)) {
            d.services.push(md.serviceType);
          }
        } else {
          data[md.facilityId] = {
            totalCosts: md.cost,
            facilityName:
              md.facility?.name ??
              facilities.data.find(({ id }) => md.facilityId === id)?.name,
            id: md.facilityId,
            services: [md.serviceType],
          };
        }
      });
      return Object.values(data);
    } else {
      return [];
    }
  }, [facilities.data, getSelectedMonthlyDetails]);

  useEffect(() => {
    if (isError) {
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    }
  }, [isError, snackbarHandler]);

  const handleChange = (budgetCopy: IBudget) => {
    budgetCopy.total =
      budgetCopy.monthlyDetails?.reduce((acc, val) => acc + val.cost, 0) ?? 0;
    onChange(budgetCopy);
  };

  return (
    <FormCard
      header={
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{`${dateFormatter(
            new Date(fiscalYear, Number(fiscalMonth), 1),
          )} Budget`}</Typography>
          <Typography
            variant="subtitle1"
            fontWeight={500}
            sx={{
              border: '2px solid',
              borderColor: 'divider',
              padding: 1,
            }}
          >
            Budget Total: {numberFormatter(budget.total)}
          </Typography>
        </Box>
      }
    >
      <FiscalMonthSelector
        fiscalMonths={monthlyCosts}
        onMonthSelected={(fMonth) => setFiscalMonthSelected(fMonth)}
      />
      <Divider>Monthly Costs Per Facility</Divider>
      <ViewMonthDetails
        fiscalMonthSelected={fiscalMonthSelected}
        monthlyFacilityCosts={monthlyFacilityDetails}
        addFacilityBtn={
          <Button
            color="secondary"
            disabled={isReadonly || fiscalMonthSelected === undefined}
            onClick={() => {
              setFormRows([]);
              setFormFacilityId('');
              setFacilityForm(true);
            }}
            startIcon={<AddIcon />}
            size="small"
            variant="outlined"
          >
            Add Facility
          </Button>
        }
        onEditClick={(id) => {
          const rows = getSelectedMonthlyDetails().filter(
            (md) => md.facilityId === id,
          );
          setFormRows(rows);
          setFormFacilityId(id);
          setFacilityForm(true);
        }}
        onDeleteClick={(id) => {
          if (fiscalMonthSelected > -1) {
            const budgetCopy = cloneDeep(budget) as IBudget;
            budgetCopy.monthlyDetails = budgetCopy.monthlyDetails.filter(
              (md) => {
                const allowed =
                  md.facilityId === id && md.month === fiscalMonthSelected;
                return !allowed;
              },
            );
            handleChange(budgetCopy);
          }
        }}
      />
      <AddFacilityForm
        key={`${formFacilityId || 'new'}-${formRows
          .map(({ serviceType, cost }) => `${serviceType}:${cost}`)
          .join('|')}`}
        facilityId={formFacilityId}
        facilities={facilities.data}
        month={fiscalMonthSelected}
        open={showFacilityForm}
        onClose={() => setFacilityForm(false)}
        onSave={(data) => {
          if (fiscalMonthSelected > -1 && data && data.length) {
            const [firstRow] = data;
            if (!firstRow?.facilityId) {
              return;
            }

            const budgetCopy = cloneDeep(budget) as IBudget;
            const monthlyDetails: IMonthlyDetail[] = data.map((row) => ({
              budgetId: budgetCopy.id,
              cost: row.cost,
              facility: row.facility ?? {},
              facilityId: row.facilityId ?? '',
              month: row.month,
              serviceType: row.serviceType,
            }));

            budgetCopy.monthlyDetails = [
              ...budgetCopy.monthlyDetails.filter(
                (md) =>
                  md.facilityId !== firstRow.facilityId ||
                  md.month !== fiscalMonthSelected,
              ),
              ...monthlyDetails,
            ];

            handleChange(budgetCopy);
            setFacilityForm(false);
            setFormRows([]);
            setFormFacilityId('');
          }
        }}
        rows={formRows}
      />
    </FormCard>
  );
};
